import React, { forwardRef } from 'react';

/**
 * EGGS Radio Component
 * Based on EGGS Design System specifications
 * 
 * @param {string} size - lg | md | sm
 * @param {string} label - Radio label
 * @param {string} helperText - Helper text below radio
 * @param {string} errorText - Error message (overrides helperText)
 * @param {boolean} required - Required field indicator
 * @param {boolean} disabled - Disabled state
 * @param {string} className - Additional CSS classes
 * @param {object} props - Additional radio props
 */
const Radio = forwardRef(({
  size = 'md',
  label,
  helperText,
  errorText,
  required = false,
  disabled = false,
  className = '',
  ...props
}, ref) => {
  const isError = errorText;

  // Base classes for radio container
  const containerClasses = [
    'relative',
    'flex',
    'items-start',
    'gap-3'
  ].join(' ');

  // Radio wrapper classes
  const radioWrapperClasses = [
    'relative',
    'flex',
    'items-center',
    'justify-center',
    'flex-shrink-0',
    'border-2',
    'rounded-full',
    'transition-all',
    'duration-200',
    'ease-in-out',
    'cursor-pointer',
    disabled ? 'opacity-50 cursor-not-allowed' : '',
    props.checked ? 'bg-white border-eggs-yellow' : 'bg-white border-gray-300',
    !disabled && !props.checked ? 'hover:border-eggs-yellow' : '',
    isError ? 'border-error' : ''
  ].join(' ');

  // Inner dot classes
  const dotClasses = [
    'rounded-full',
    'bg-eggs-yellow',
    'transition-all',
    'duration-200',
    'ease-in-out',
    props.checked ? 'scale-100' : 'scale-0'
  ].join(' ');

  // Size-specific classes
  const sizeClasses = {
    lg: {
      radio: 'w-6 h-6',
      dot: 'w-3 h-3',
      label: 'text-lg',
      helper: 'text-sm'
    },
    md: {
      radio: 'w-5 h-5',
      dot: 'w-2.5 h-2.5',
      label: 'text-base',
      helper: 'text-sm'
    },
    sm: {
      radio: 'w-4 h-4',
      dot: 'w-2 h-2',
      label: 'text-sm',
      helper: 'text-xs'
    }
  };

  // Label classes
  const labelClasses = [
    'font-burns',
    'font-extralight',
    'text-eggs-dark',
    'cursor-pointer',
    'select-none',
    sizeClasses[size].label,
    disabled ? 'opacity-50 cursor-not-allowed' : ''
  ].join(' ');

  // Helper/error text classes
  const helperTextClasses = [
    'mt-1',
    'font-burns',
    'font-extralight',
    sizeClasses[size].helper,
    isError ? 'text-error' : 'text-gray-500'
  ].join(' ');

  const displayText = errorText || helperText;

  return (
    <div className={`${containerClasses} ${className}`}>
      <div className="flex items-start gap-3">
        <div className={`${radioWrapperClasses} ${sizeClasses[size].radio}`}>
          <input
            ref={ref}
            type="radio"
            className="sr-only"
            disabled={disabled}
            {...props}
          />
          
          <div className={`${dotClasses} ${sizeClasses[size].dot}`} />
        </div>
        
        <div className="flex-1">
          {label && (
            <label className={labelClasses}>
              {label}
              {required && <span className="text-error ml-1">*</span>}
            </label>
          )}
          
          {displayText && (
            <p className={helperTextClasses}>
              {displayText}
            </p>
          )}
        </div>
      </div>
    </div>
  );
});

Radio.displayName = 'Radio';

/**
 * EGGS RadioGroup Component
 * Container for multiple radio options
 */
export const RadioGroup = ({ 
  label, 
  required = false, 
  errorText, 
  helperText, 
  children, 
  className = '',
  size = 'md'
}) => {
  const isError = errorText;

  // Label classes
  const labelClasses = [
    'block',
    'font-burns',
    'font-normal',
    'text-eggs-dark',
    'mb-3',
    size === 'lg' ? 'text-base' : size === 'md' ? 'text-sm' : 'text-xs'
  ].join(' ');

  // Helper/error text classes
  const helperTextClasses = [
    'mt-2',
    'font-burns',
    'font-extralight',
    size === 'lg' ? 'text-sm' : 'text-xs',
    isError ? 'text-error' : 'text-gray-500'
  ].join(' ');

  const displayText = errorText || helperText;

  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <div className={labelClasses}>
          {label}
          {required && <span className="text-error ml-1">*</span>}
        </div>
      )}
      
      <div className="space-y-3">
        {children}
      </div>
      
      {displayText && (
        <p className={helperTextClasses}>
          {displayText}
        </p>
      )}
    </div>
  );
};

export default Radio;